import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { FileText } from "lucide-react";
import { ficharService } from "@/services/ficharService";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import SignaturePad from "react-signature-canvas";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { sanitizeUrl } from "@braintree/sanitize-url";
import logo from "@/assets/logo.png";
import logoDark from "@/assets/logo-dark.png";
import { getSession } from "@/lib/session";

interface Documento {
  hash: string;
  start?: string;
  end?: string;
  signed: "Y" | "N";
  pdf?: string;
  title?: string;
  value?: string;
}

const Documentos = () => {
  const [isAndroid, setIsAndroid] = useState(false);
  const [documentosPendientes, setDocumentosPendientes] = useState<Documento[]>([]);
  const [documentosFirmados, setDocumentosFirmados] = useState<Documento[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState<Documento | null>(null);
  const [iframeUrl, setIframeUrl] = useState<string>("");
  const [userHash, setUserHash] = useState<string | null>(null);
  const [showModalFirma, setShowModalFirma] = useState(false);
  const [sigPad, setSigPad] = useState<any>(null);


  const baseUrl = import.meta.env.VITE_BASE_URL;

  useEffect(() => {
    const ua = navigator.userAgent || navigator.vendor || (window as any).opera;
    if (/android/i.test(ua)) {
      setIsAndroid(true);
    }
  }, []);
    
  // 👉 Cargar documentos al iniciar
  useEffect(() => {
    getSession().then((parsedUser) => {
      if (!parsedUser) return;

      const userId = parsedUser?.id;
      if (!userId) return;

      setLoading(true);
      ficharService.getDataEmployee(userId)
        .then((response: any) => {
          const datosResponse = response.response;
          setUserHash(datosResponse.hash);

          // Cargar reporte actual en iframe
          setIframeUrl(`${baseUrl}reporte/filtrar/${datosResponse.hash}`);

          return ficharService.getPeriods(datosResponse.hash);
        })
        .then((docs: any) => {
          const documentos = docs.response;
          setDocumentosPendientes(documentos.filter((d: Documento) => d.hash && d.signed === "N"));
          setDocumentosFirmados(documentos.filter((d: Documento) => d.hash && d.signed === "Y"));
        })
        .catch((err) => console.error(err))
        .finally(() => setLoading(false));
    });
  }, []);

  const handleSelectDocumento = (doc: Documento) => {
    setSelectedDoc(doc);
    if (doc.signed == "N" && doc.start && doc.end) {
        setIframeUrl(sanitizeUrl(`${baseUrl}reporte/resumen/${userHash}/${doc.start}/${doc.end}/?iframe=true`));
    } else if (doc.pdf) {
        setIframeUrl(sanitizeUrl(`${doc.pdf}#toolbar=1&navpanes=0&scrollbar=1&view=FitV`));
    }
  };

  const handleTabChange = () => {
    console.log("handleTabChange");
    setSelectedDoc(null);
    setIframeUrl("");
  };

  const guardarFirma = async () => {
    if (!sigPad || sigPad.isEmpty()) {
      toast({
        title: "Error",
        description: "Debes firmar antes de continuar",
        variant: "destructive",
      });
      return;
    }

    const img = sigPad.toDataURL();

    setLoading(true);

    try {
      const response = await ficharService.inserSignature({
        id: selectedDoc?.hash,
        pngsignatue: img
      });

      console.log("response", response);

      setShowModalFirma(false);

      // Refrescar lista
      if (userHash) {
        const docs = await ficharService.getPeriods(userHash);
        setDocumentosPendientes(docs.response.filter((d: Documento) => d.hash && d.signed === "N"));
        setDocumentosFirmados(docs.response.filter((d: Documento) => d.hash && d.signed === "Y"));
      }
      setSelectedDoc(null);
      setIframeUrl("");

    } catch (e) {
      console.log("error", e);
    }

    setLoading(false);
  };

  if (loading) {
    return (
      <div className="fixed inset-0 flex flex-col items-center justify-center bg-white dark:bg-gray-900 z-50">
        <img
          src={logo}
          alt="Logo"
          className="h-20 w-auto mb-6 dark:hidden"
        />
        <img src={logoDark} alt="Logo" className="h-20 w-auto mb-4 hidden dark:block" />
        <div className="w-10 h-10 border-4 border-gray-300 border-t-blue-500 rounded-full animate-spin"></div>
        <p className="mt-4 text-gray-500 text-sm">Cargando...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">

      <Card className="border-0 shadow-lg">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl">Reportes</CardTitle>
              <CardDescription className="text-base mt-1">Tus reportes de registros de trabajo (Fichajes)</CardDescription>
            </div>
            <FileText className="w-8 h-8 text-primary" />
          </div>
        </CardHeader>

        <CardContent>
          <Tabs defaultValue="actual">
            <TabsList>
              <TabsTrigger onClick={handleTabChange} value="actual">Actual</TabsTrigger>
              <TabsTrigger onClick={handleTabChange} value="pendientes">Pendientes ({documentosPendientes.length})</TabsTrigger>
              <TabsTrigger onClick={handleTabChange} value="firmados">Firmados ({documentosFirmados.length})</TabsTrigger>
            </TabsList>

            {/* Pestaña Actual */}
            <TabsContent value="actual">
              {userHash ? (
                <div className="mt-4 h-[600px] w-full rounded-xl overflow-hidden border">
                  <iframe
                    src={sanitizeUrl(`${baseUrl}reporte/resumen/${userHash}?iframe=true`)}
                    className="w-full h-full"
                    title="Reporte Actual"
                  ></iframe>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">Cargando reporte actual...</p>
              )}
            </TabsContent>

            {/* Pestaña Pendientes */}
            <TabsContent value="pendientes">
              {documentosPendientes.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay documentos pendientes</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documentosPendientes.map((doc) => (
                    <Card key={doc.hash} className="border shadow-sm hover:shadow-md cursor-pointer" onClick={() => handleSelectDocumento(doc)}>
                      <CardHeader>
                        <CardTitle>{doc.value}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Estado: {doc.signed === "N" ? "Pendiente" : "Firmado"}
                        </p>
                        <Button className="mt-2 w-full" onClick={() => handleSelectDocumento(doc)}>
                          {doc.signed === "N" ? "Ver / Firmar" : "Ver"}
                        </Button>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Pestaña Firmados */}
            <TabsContent value="firmados">
              {documentosFirmados.length === 0 ? (
                <p className="text-center text-muted-foreground py-8">No hay documentos firmados</p>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {documentosFirmados.map((doc) => (
                    <Card key={doc.hash} className="border shadow-sm hover:shadow-md cursor-pointer" onClick={() => handleSelectDocumento(doc)}>
                      <CardHeader>
                        <CardTitle>{doc.value}</CardTitle>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground">
                          Estado: {doc.signed === "Y" ? "Firmado" : "Pendiente"}
                        </p>
                        { !isAndroid && (
                          <Button className="mt-2 w-full" onClick={() => handleSelectDocumento(doc)}>
                            Ver
                          </Button>
                        )}
                        { isAndroid && doc.signed === "Y" && doc.pdf && (
                          <Button className="mt-2 w-full" asChild>
                              <a
                                href={sanitizeUrl(doc.pdf)}
                                download
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                Descargar PDF
                              </a>
                            </Button>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* IFRAME para mostrar documento seleccionado */}
            {selectedDoc && iframeUrl && (
              <div className="mt-6">
                <div className="h-[600px] w-full rounded-xl overflow-hidden border">
                  <iframe
                    src={iframeUrl}
                    className="w-full h-full"
                    title="Documento"
                  ></iframe>
                </div>

                {/* Si NO está firmado → botón Firmar */}
                {selectedDoc.signed === "N" && (
                  <div className="w-full text-right mt-3">
                    <Button onClick={() => setShowModalFirma(true)}>Firmar</Button>
                  </div>
                )}

                {/* Si está firmado → botón Descargar */}
                {selectedDoc.signed === "Y" && selectedDoc.pdf && (
                  <div className="w-full text-right mt-3">
                    <Button asChild>
                      <a
                        href={sanitizeUrl(selectedDoc.pdf)}
                        download
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        Descargar PDF
                      </a>
                    </Button>
                  </div>
                )}
              </div>
            )}

          </Tabs>
        </CardContent>
      </Card>
      {/* MODAL DE FIRMA */}
      <Dialog open={showModalFirma} onOpenChange={setShowModalFirma}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Firma Digital</DialogTitle>
          </DialogHeader>

          <div className="w-full max-w-[420px] mx-auto">
            <SignaturePad
              ref={(ref) => setSigPad(ref)}
              penColor="black"
              canvasProps={{
                className: "w-full h-[220px] sm:h-[300px] border rounded",
              }}
            />
            <div className="flex w-full mt-3 gap-3">
              <Button variant="destructive" className="flex-1" onClick={() => setShowModalFirma(false)}>
                Cancelar
              </Button>
              <Button variant="secondary" className="flex-1" onClick={() => sigPad.clear()}>
                Limpiar
              </Button>
              <Button className="flex-1" onClick={guardarFirma}>
                Firmar
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Documentos;
